import { useQuery } from '@tanstack/react-query';

import { typedApiClient } from '~/common/util/api-client';
import {
  CalculationTypes,
  REQUEST_REFERENCE_TYPE,
} from '~/pages/IncomeCalculator/enums';
import { useIncomeIdentifier } from '~/pages/IncomeCalculator/hooks';
import type {
  AddressIdentifier,
  CalculationResult,
  IncomeIdentifier,
  W2CalculationResult,
  WvoeCalculationResult,
} from '~/pages/IncomeCalculator/types';
import { commonKeys } from '~/pages/IncomeCalculator/utils';

type CalculationResponse = Awaited<
  ReturnType<typeof typedApiClient.v1.incomeCalculatorGetCalculations>
>['data'];
type CalculationItem = Omit<
  CalculationResponse['genericIncomeData'][number],
  'calculationItems'
> &
  CalculationResponse['genericIncomeData'][number]['calculationItems'][number];
type WageEarnerResponse =
  CalculationResponse['wageEarnerData'][number]['calculationItems'][number];
type VoeResponse =
  CalculationResponse['voeData'][number]['calculationItems'][number];
type GenericResponse =
  CalculationResponse['genericIncomeData'][number]['calculationItems'][number];

export const calculationKeys = {
  all: [...commonKeys.all, 'calculation'],
  withIncome: (identifier: IncomeIdentifier | AddressIdentifier) => [
    ...calculationKeys.all,
    ...commonKeys.withIdentifier(identifier),
  ],
  calculationId: (
    identifier: IncomeIdentifier | AddressIdentifier,
    calculationId: number,
  ) => [...calculationKeys.withIncome(identifier), calculationId],
  latestCalculation: (identifier: IncomeIdentifier | AddressIdentifier) => [
    ...calculationKeys.withIncome(identifier),
    'latest',
  ],
};

export function isWageEarner(
  result: CalculationResult,
): result is W2CalculationResult {
  return result?.calculatorTypeId === CalculationTypes.PayStub;
}

export function isVoe(
  result: CalculationResult,
): result is WvoeCalculationResult {
  return (
    result?.calculatorTypeId === CalculationTypes.Wvoe ||
    result?.calculatorTypeId === CalculationTypes.VoeWithPaystub ||
    result?.calculatorTypeId === CalculationTypes.YearEndPaystub
  );
}

export default function useCalculation(
  calculationId?: number,
  calculationAuditId?: number,
  employer?: IncomeIdentifier,
  property?: AddressIdentifier,
  enabled: boolean = true,
) {
  const { obfuscatedLoanIdentifier, incomeIdentifier, addressIdentifier } =
    useIncomeIdentifier();

  const incomeId = employer ?? incomeIdentifier;
  const addressId = property ?? addressIdentifier;

  return useQuery({
    queryKey: calculationId
      ? calculationKeys.calculationId(incomeId || addressId, calculationId)
      : calculationKeys.latestCalculation(incomeId || addressId),
    async queryFn() {
      if (!incomeId && !addressId) {
        throw new Error('No income source provided');
      }

      const { data } = await typedApiClient.v1.incomeCalculatorGetCalculations({
        obfuscatedLoanIdentifier,
        incomeIdentifier: incomeId,
        rentalAddressId: addressId?.addressId,
        calculationId: calculationId ?? 0,
        calculationAuditId: calculationAuditId ?? 0,
        requestReferenceType: incomeId
          ? REQUEST_REFERENCE_TYPE.employmentIncome
          : REQUEST_REFERENCE_TYPE.rentalIncome,
      });

      return mapCalculation(data);
    },
    enabled,
  });
}

/** @internal */
export function mapCalculation(data: CalculationResponse) {
  const { genericIncomeData, voeData, wageEarnerData, ...rest } = data;

  const results = [...genericIncomeData, ...wageEarnerData, ...voeData].flatMap(
    ({ calculationItems, ...result }) =>
      calculationItems.map(
        (item: VoeResponse | WageEarnerResponse | GenericResponse) => {
          return {
            ...result,
            ...item,
            loanCalculationId: data.loanCalculationId,
            calculatorTypeId:
              (item as GenericResponse)?.incomeCalculatorTypeId ??
              result.calculatorTypeId,
            comments: data.comment,
            formulas: Object.hasOwn(item, 'formulas')
              ? (item as GenericResponse).formulas
              : [],
            getFiscalYears: getFiscalYears(result.inputFields),
            getField: getField(item.calculationFields),
            getInput: getField(result.inputFields),
            getInputAsFloat: getFieldAsFloat(result.inputFields),
          };
        },
      ),
  );

  return {
    ...rest,
    results,
  };
}

function getFiscalYears(
  fields: CalculationItem['calculationFields'] | CalculationItem['inputFields'],
) {
  return () => [
    ...new Set(
      fields.filter((field) => field.fiscalYear !== 0).map((f) => f.fiscalYear),
    ),
  ];
}

function getField(
  fields: CalculationItem['calculationFields'] | CalculationItem['inputFields'],
) {
  return (
    formType: number,
    fiscalYear: number | null,
    fieldName: string | number,
  ) =>
    fields.find(
      (field) =>
        field.formId === formType &&
        (!fiscalYear || field.fiscalYear === fiscalYear) &&
        (field.fieldName.toLowerCase() === `${fieldName}`.toLowerCase() ||
          field.fieldId === fieldName),
    );
}

function getFieldAsFloat(
  fields: CalculationItem['calculationFields'] | CalculationItem['inputFields'],
) {
  return (
    formType: number,
    fiscalYear: number | null,
    fieldName: string | number,
  ) =>
    Number.parseFloat(
      getField(fields)(formType, fiscalYear, fieldName)?.fieldValue ?? '0',
    );
}
